import { IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Suspense, lazy, memo, useCallback, useEffect } from "react";
import { PDFObject } from "react-pdfobject";
import { useDispatch, useSelector } from "react-redux";
import { A11y, Keyboard, Thumbs, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import MapHotspotCanvas from "@/Dashboard/containers/Elements/MapHotspotCanvas";
import { CONTENT_TYPE, MEDIA_SIZE, TAB_STATUSES } from "@/constants/index.js";
import { useUrlParams } from "@/hooks";
import { ArrowLeftIcon, ArrowRightIcon } from "@/icons";
import { setLoadingStatus, updateVisualBuilderData } from "@/store/actions";
import { loadingStatusSelector, visualBuilderDataSelector } from "@/store/selectors";
import { hotspotDataSelector } from "@/store/v2/hotspotData/selectors";
import { slideToIndexSelector } from "@/store/v2/swiper/selectors";
import { getMediaLink } from "@/utils/mediaLink";

import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/thumbs";
import "swiper/css/keyboard";
import "swiper/css/virtual";

const useStyles = makeStyles({
  fullscreenSwiperContainer: {
    zIndex: "0 !important",
    height: "100%",
    width: "100%",
    "--swiper-navigation-color": "#fff",
  },
  thumbnailsSwiperContainer: {
    position: "fixed !important",
    zIndex: "0 !important",
    bottom: "0px",
    width: "100%",
    height: "calc(8rem + 20px)",
    background: "rgba(216, 216, 216, 0.1)",
    "backdrop-filter": "blur(5px)",
    paddingTop: "10px !important",
    paddingBottom: "10px !important",
  },
  thumbnailsSwiperSlide: { width: "8rem !important" },
  imageContainer: {
    backgroundColor: "#000",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  linkPreview: {
    backgroundColor: "#fff",
    width: "100%",
    height: "100%",
    border: 0,
  },
  document: {
    margin: "0 auto",
    justifyContent: "center",
    marginTop: "4rem",
    height: "100%",
    width: "100%",
    "& > div": {
      height: "100%",
      overflow: "hidden !important",
    },
  },
  slideButton: {
    zIndex: 2,
    position: "absolute",
    top: "50%",
    transition: "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    backgroundColor: "rgba(0,0,0,0.7) !important",
    color: "#FFF",
  },
  previousSlide: {
    right: (props) => (props.rightDrawer && !props.rightDrawerAutoHide ? "245px" : "15px"),
  },
  nextSlide: {
    left: (props) => (props.leftDrawer && !props.leftDrawerAutoHide ? "245px" : "15px"),
  },
  noResult: {
    display: "flex",
    "justify-content": "center",
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    "justify-content": "center",
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  progressRoot: {
    width: "25%",
    height: 4,
    backgroundColor: "grey",
  },
  barPrimary: {
    backgroundColor: "white",
  },
  notEmbeddable: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
  },
  shareButton: {
    bottom: 0,
    right: 0,
    padding: "8px 12px",
    backgroundColor: "#24949D",
    color: "#fff",
    textAlign: "center",
    maxWidth: 125,
    marginBottom: "5px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "rgba(27,72,86,0.8)",
    },
  },
  audio: {
    position: "absolute",
    width: "30vw",
    minWidth: 300,
    top: "50%",
    left: "50%",
    transform: "translate(-50%)",
  },
});

const IFrameContent = lazy(() => import("./IFrameContent"));

function Content(props) {
  const { mediaItem, classes, load, setLoading } = props;
  const { contentType, name, link, id: mediaItemId } = mediaItem;

  const isIframe =
    contentType.value === CONTENT_TYPE.THREE_DIMENSIONAL_MODEL ||
    contentType.value === CONTENT_TYPE.THREE_HUNDRED_SIXTY_PHOTO ||
    contentType.value === CONTENT_TYPE.VIRTUAL_TOUR ||
    contentType.value === CONTENT_TYPE.LINK ||
    contentType.value === CONTENT_TYPE.VIDEO;

  if (load) {
    if (mediaItem.link && contentType.value === CONTENT_TYPE.PHOTO) {
      const fullSrc = getMediaLink({ size: MEDIA_SIZE.xsupersize, link: mediaItem.link });
      const largeSrc = getMediaLink({ size: MEDIA_SIZE.large, link: mediaItem.link }); // 960px
      const mediumSrc = getMediaLink({ size: MEDIA_SIZE.medium, link: mediaItem.link }); // 720px

      return (
        <div className={classes.imageContainer}>
          <img
            srcSet={`${mediumSrc} 720w, ${largeSrc} 960w, ${fullSrc} 1920w`}
            className={classes.image}
            alt={name}
            onLoad={() => setLoading(false)}
          />
        </div>
      );
    }

    if (link) {
      // Runtime embeddability check is now handled in IFrameContent component
      if (isIframe) {
        return (
          <Suspense>
            <IFrameContent
              className={classes.linkPreview}
              setLoading={setLoading}
              src={link}
              title={contentType.value}
            />
          </Suspense>
        );
      }

      if (contentType.value === CONTENT_TYPE.PDF) {
        return (
          <div className={classes.document}>
            <PDFObject
              url={getMediaLink({ link })}
              width="100%"
              height="100%"
              containerId={`pdf-viewer-${mediaItemId}`}
              PDFJS_URL="/pdf-viewer/web/viewer.html"
              forcePDFJS
              containerProps={{ style: { height: "100%" } }}
            />
          </div>
        );
      }

      if (contentType.value === CONTENT_TYPE.AUDIO) {
        return (
          <audio
            className={classes.audio}
            src={getMediaLink({ link })}
            controls
            onLoadStart={() => setLoading(false)}
            aria-label={`${name}-audio`}
          >
            <track kind="captions" />
          </audio>
        );
      }
    }
  }

  return null;
}

export const ContentMemo = memo((props) => {
  const { classes, mediaItem, load, setLoading } = props;

  return <Content classes={classes} mediaItem={mediaItem} load={load} setLoading={setLoading} />;
});

export default function FullscreenCarousel(props) {
  const {
    propertyId,
    experienceId,
    gridView,
    filteredMediaItems = [],
    leftDrawer,
    rightDrawer,
    thumbsSwiper,
    slideNumber,
    leftDrawerAutoHide,
    rightDrawerAutoHide,
    tabStatus,
    swiperRef,
    isSalesPerson,
    isTemplate,
  } = props;

  const dispatch = useDispatch();

  const setLoading = useCallback(
    (contentLoading) => {
      dispatch(setLoadingStatus({ contentLoading }));
    },
    [dispatch],
  );
  const params = useUrlParams();
  const {
    visualBuilderData: { locked },
  } = useSelector(visualBuilderDataSelector(params));
  const { fromHotspot } = useSelector(hotspotDataSelector(propertyId)) || {};
  const { slideToIndex } = useSelector(slideToIndexSelector());
  const { dashboardLoading } = useSelector(loadingStatusSelector);

  useEffect(() => {
    if (slideToIndex !== undefined) {
      const index = filteredMediaItems?.findIndex((item) => item.id === slideToIndex);

      if (index >= 0) {
        slideTo(index);
      }
    }
  }, [slideToIndex, filteredMediaItems]);

  const slideTo = (index) => {
    swiperRef.current?.swiper.slideTo(index);
  };

  const onSlideChange = useCallback(
    (swiperCore) => {
      const { activeIndex } = swiperCore;
      if (!fromHotspot && tabStatus !== "share" && filteredMediaItems.length) {
        const values = { currentMediaItemId: filteredMediaItems[activeIndex].id };
        dispatch(updateVisualBuilderData(params, values, {}, true));
      }
    },
    [dispatch, params, fromHotspot, tabStatus, filteredMediaItems],
  );

  const classes = useStyles({
    ...props,
    ...(leftDrawer && { leftDrawer }),
    ...(rightDrawer && { rightDrawer }),
    ...(tabStatus && { tabStatus }),
    ...(leftDrawerAutoHide && { leftDrawerAutoHide }),
    ...(rightDrawerAutoHide && { rightDrawerAutoHide }),
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    swiperRef.current?.swiper.slideTo(slideNumber, 0);
  }, [slideNumber, dashboardLoading]);

  const ButtonGroup = () => {
    return (
      filteredMediaItems.length > 1 && (
        <>
          <IconButton
            data-testid="back-arrow"
            aria-label="Go to previous slide"
            className={`${classes.slideButton} ${classes.nextSlide} `}
            onClick={() => {
              if (swiperRef.current?.swiper.isBeginning) {
                swiperRef.current?.swiper.slideTo(filteredMediaItems.length);
              } else {
                swiperRef.current?.swiper.slidePrev();
              }
            }}
            size="large"
            disabled={(locked || (isSalesPerson && isTemplate)) && tabStatus === TAB_STATUSES.CUSTOMIZE}
          >
            <ArrowLeftIcon fontSize="large" htmlColor="#fff" />
          </IconButton>
          <IconButton
            data-testid="next-arrow"
            aria-label="Go to next slide"
            className={`${classes.slideButton} ${classes.previousSlide} `}
            onClick={() => {
              if (swiperRef.current?.swiper.isEnd) {
                swiperRef.current?.swiper.slideTo(0);
              } else {
                swiperRef.current?.swiper.slideNext();
              }
            }}
            size="large"
            disabled={(locked || (isSalesPerson && isTemplate)) && tabStatus === TAB_STATUSES.CUSTOMIZE}
          >
            <ArrowRightIcon fontSize="large" htmlColor="#fff" />
          </IconButton>
        </>
      )
    );
  };

  return (
    <>
      <MapHotspotCanvas experienceId={experienceId} propertyId={propertyId} />
      <ButtonGroup />
      <Swiper
        modules={[A11y, Keyboard, Thumbs, Virtual]}
        ref={swiperRef}
        slidesPerView={1}
        centeredSlides
        className={classes.fullscreenSwiperContainer}
        thumbs={{ swiper: thumbsSwiper }}
        grabCursor
        keyboard={{ enabled: true }}
        onSlideChange={(swiperCore) => onSlideChange(swiperCore)}
        virtual
      >
        {!gridView &&
          filteredMediaItems.length &&
          filteredMediaItems.map((mediaItem, index) => {
            return (
              <SwiperSlide key={`fullscreen-${mediaItem.id}`} className={classes.swiperSlide} virtualIndex={index}>
                {({ isActive, isNext, isPrev }) => (
                  <ContentMemo
                    load={
                      ((isNext || isPrev) &&
                        mediaItem.contentType.value !== CONTENT_TYPE.LINK &&
                        mediaItem.contentType.value !== CONTENT_TYPE.VIDEO) ||
                      ((isNext || isPrev) &&
                        (mediaItem.contentType.value === CONTENT_TYPE.PHOTO ||
                          mediaItem.contentType.value === CONTENT_TYPE.PDF)) ||
                      isActive
                    }
                    classes={classes}
                    mediaItem={mediaItem}
                    setLoading={setLoading}
                  />
                )}
              </SwiperSlide>
            );
          })}
        {filteredMediaItems.length === 0 && (
          <SwiperSlide key="fullscreen-no-result">
            <div className={classes.noResult}>
              <Typography style={{ opacity: 0.5, display: "block", padding: 0 }} color="secondary">
                {`Oops! It looks like there's nothing to view here anymore. If you think this is a mistake, please contact
                this property for assistance.`}
              </Typography>
            </div>
          </SwiperSlide>
        )}
      </Swiper>
    </>
  );
}
